import { projectFromDocument, serializeProjectDocument, type YxpdDocument } from '../core/projectFile';
import { dedupeProjectsByUid, listProjects, saveProject } from '../storage/projects';
import type { PatternProject } from '../types';

interface CloudSummary { projectUid: string; revision: number; updatedAt: number; }

async function api<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, { ...options, credentials: 'include', headers: { 'Content-Type': 'application/json', ...(options?.headers || {}) } });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || '多端同步失败');
  return data;
}

export async function pushCloudProject(project: PatternProject) {
  await api(`/api/projects/${encodeURIComponent(project.projectUid)}`, { method: 'PUT', body: JSON.stringify({ document: serializeProjectDocument(project) }) });
}

const pending = new Map<string, number>();
export function queueCloudProject(project: PatternProject) {
  const previous = pending.get(project.projectUid);
  if (previous) window.clearTimeout(previous);
  const timer = window.setTimeout(() => {
    pending.delete(project.projectUid);
    void pushCloudProject(project).catch(() => undefined);
  }, 1800);
  pending.set(project.projectUid, timer);
}

export async function removeCloudProject(projectUid: string) {
  await api(`/api/projects/${encodeURIComponent(projectUid)}`, { method: 'DELETE' });
}

export async function removeCloudProjects(projectUids: string[]) {
  const unique = [...new Set(projectUids.filter(Boolean))];
  if (!unique.length) return;
  for (let index = 0; index < unique.length; index += 100) {
    await api('/api/projects/bulk-delete', { method: 'POST', body: JSON.stringify({ projectUids: unique.slice(index, index + 100) }) });
  }
}

export async function syncCloudProjects(ownerKey: string): Promise<PatternProject[]> {
  const locals = await listProjects(ownerKey);
  const response = await api<{ projects: CloudSummary[] }>('/api/projects');
  const remoteByUid = new Map(response.projects.map((item) => [item.projectUid, item]));
  const merged = dedupeProjectsByUid(locals);
  for (const remote of response.projects) {
    const index = merged.findIndex((item) => item.projectUid === remote.projectUid);
    const local = index >= 0 ? merged[index] : null;
    if (!local || remote.updatedAt > local.updatedAt + 1000) {
      const detail = await api<{ project: { document: YxpdDocument; updatedAt: number } }>(`/api/projects/${encodeURIComponent(remote.projectUid)}`);
      const project = projectFromDocument(detail.project.document, ownerKey, '云端同步', { id: local?.id, projectUid: remote.projectUid, preserveTimes: true });
      if (local?.sourceImage) project.sourceImage = local.sourceImage;
      if (local?.processOptions) project.processOptions = { ...local.processOptions };
      project.updatedAt = remote.updatedAt;
      await saveProject(project);
      if (index >= 0) merged[index] = project; else merged.push(project);
    }
  }
  for (const local of merged) {
    const remote = remoteByUid.get(local.projectUid);
    if (!remote || local.updatedAt > remote.updatedAt + 1000) await pushCloudProject(local);
  }
  return dedupeProjectsByUid(await listProjects(ownerKey));
}
